<script setup lang="ts">
import { XModal, XButton, XRadio, XTextArea } from '@clicksign/excalibur';
import { useI18n } from '#i18n';

type PixFeedbackModalEmit = {
  (e: 'close'): void;
  (e: 'send-feedback', value: string): void;
};

const { t } = useI18n();

const emit = defineEmits<PixFeedbackModalEmit>();

function closeModal() {
  emit('close');
}

const options = ref([
  {
    text: t('requirement.pix.sign.feedbackModal.options.dontHavePIX'),
    value: t('requirement.pix.sign.feedbackModal.options.dontHavePIX'),
    dataTestid: 'feedbackOptionDontHavePIXField',
  },
  {
    text: t('requirement.pix.sign.feedbackModal.options.cantPay'),
    value: t('requirement.pix.sign.feedbackModal.options.cantPay'),
    dataTestid: 'feedbackCantPayField',
  },
  {
    text: t('requirement.pix.sign.feedbackModal.options.dontWantToSign'),
    value: t('requirement.pix.sign.feedbackModal.options.dontWantToSign'),
    dataTestid: 'feedbackDontWantToSignField',
  },
  {
    text: t('requirement.pix.sign.feedbackModal.options.cantUnderstand'),
    value: t('requirement.pix.sign.feedbackModal.options.cantUnderstand'),
    dataTestid: 'feedbackCantUnderstandField',
  },
  {
    text: t('requirement.pix.sign.feedbackModal.options.issuesWithMyBank'),
    value: t('requirement.pix.sign.feedbackModal.options.issuesWithMyBank'),
    dataTestid: 'feedbackIssuesWithMyBankField',
  },
  {
    text: t('requirement.pix.sign.feedbackModal.options.other'),
    value: 'other',
    dataTestid: 'feedbackOtherField',
  },
]);

const isTextAreaShown = ref(false);

const observation = ref('');

function selectOption(event: string) {
  if (event === 'other') {
    observation.value = '';
    isTextAreaShown.value = true;

    return;
  }

  observation.value = event;
  isTextAreaShown.value = false;
}

function sendFeedback() {
  emit('send-feedback', observation.value);
}
</script>

<template>
  <XModal
    :title="t('requirement.pix.sign.feedbackModal.title')"
    is-open
    data-testid="pixFeedbackModal"
    @close="closeModal"
  >
    <XRadio
      :items="options"
      column
      @update:model-value="selectOption"
    />

    <XTextArea
      v-if="isTextAreaShown"
      v-model="observation"
      :class="$style.textArea"
      data-testid="feedbackTextArea"
    />

    <template #actions>
      <XButton
        design="outlined"
        data-testid="cancelSendFeedbackButton"
        @click="closeModal"
      >
        {{ t('common.cancel') }}
      </XButton>
      <XButton
        :disabled="!observation"
        data-testid="sendFeedbackButton"
        @click="sendFeedback"
      >
        {{ t('common.next') }}
      </XButton>
    </template>
  </XModal>
</template>

<style lang="scss" module>
.textArea {
  margin-top: var(--space-regular);
}
</style>
